import { useSellarUser } from "../../hooks";
import { useOrder } from "../../hooks/api/v2/order/useOrder";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { CancelOrderDialog } from "../../pages";
import { SellarCreditCancelWarningDialog } from "../../pages/order/modals/SellarCreditCancelWarningDialog";
import { ActionPanel } from "../actionPanel/ActionPanel";
import { useState } from "react";
import { Icon } from "@sellar-io/components";
import { SellarCreditAddToTabButton } from "./Buttons/SellarCreditAddToTabButton";
import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import { useSellarCreditPaymentStatus } from "./useSellarCreditPaymentStatus";

type PayWithSellarCreditActionPanelProps = {
  orderId: number;
  orderDeferredPaymentId: string | null;
  initiator: "supplier" | "retailer" | null;
  supplierName: string;
  twoOrder: OrderGetResponseType["TwoOrder"];
};

export const PayWithSellarCreditActionPanel = ({
  orderId,
  orderDeferredPaymentId,
  initiator,
  supplierName,
}: PayWithSellarCreditActionPanelProps) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useSellarUser();
  const [cancelOrderModalActive, setCancelOrderModalActive] =
    useState<boolean>(false);
  const [sellarCreditCancelWarningActive, setSellarCreditCancelWarningActive] =
    useState<boolean>(false);
  const orderQuery = useOrder(orderId);
  const { isProcessed } = useSellarCreditPaymentStatus({
    orderId,
  });

  if (isProcessed) {
    return null;
  }

  const isSupplierInitiated = initiator === "supplier";
  const title = isSupplierInitiated
    ? `Order confirmation request from ${supplierName}`
    : "Payment using Sellar Credit not processed";
  const description = isSupplierInitiated
    ? `Confirm this order from ${supplierName} and put it on your tab using the button below`
    : "Your payment using Sellar Credit was not processed. Try your payment again using the button below";

  const userCantAccessSellarCredit =
    !business?.BusinessUser?.canAccessSellarCredit;
  return (
    <>
      <ActionPanel className="flex flex-col gap-4" title={title}>
        <div className="flex flex-col gap-4">
          <p>{description}</p>
          <div className="flex flex-row gap-2">
            <SellarCreditAddToTabButton
              orderId={orderId}
              orderDeferredPaymentId={orderDeferredPaymentId}
              initiator={initiator}
              isDisabled={userCantAccessSellarCredit}
            />
          </div>
          {userCantAccessSellarCredit && (
            <div className="flex flex-row items-center gap-1 text-red-500">
              <Icon icon={"INFORMATION"} />
              {user?.email} doesn't have Sellar Credit access
            </div>
          )}
        </div>
      </ActionPanel>
      <SellarCreditCancelWarningDialog
        active={sellarCreditCancelWarningActive}
        type={business?.Supplier ? "supplier" : "buyer"}
        onEditOrder={
          business?.Retailer
            ? () => {
                setSellarCreditCancelWarningActive(false);
              }
            : undefined
        }
        onDismiss={() => {
          setSellarCreditCancelWarningActive(false);
        }}
        onConfirmCancel={() => {
          setSellarCreditCancelWarningActive(false);
          setCancelOrderModalActive(true);
        }}
      />
      {orderQuery.data && (
        <CancelOrderDialog
          active={cancelOrderModalActive}
          order={orderQuery.data}
          onDismiss={() => setCancelOrderModalActive(false)}
        />
      )}
    </>
  );
};
